/**
 * Self-fetching leave policy groups hook (V370 leave entitlement engine).
 *
 * A policy group lets some employees receive a different leave policy from
 * the leave type's default (e.g. "Managers" get 21 annual days while
 * everyone else keeps the 14-day default). Backed by:
 *   - `payroll.listLeavePolicyGroups`       — groups + rules + member ids,
 *   - `payroll.upsertLeavePolicyGroup`      — create/update, replaces rules,
 *   - `payroll.setEmployeeLeavePolicyGroup` — move an employee in/out.
 * Every write re-applies the entitlement engine server-side, so balances
 * follow immediately (additive true-ups; manual adjustments preserved).
 *
 * Mirrors `use_leave_types.ts`'s DI-seam + state-machine pattern: `upsert` /
 * `assign` are `undefined` unless the read resolved `ready` AND the grant is
 * write-capable.
 */
import * as React from 'react';
import {
  getTrpcClient,
  isForbiddenTrpcError,
  isPayrollAddonRequiredError,
} from '../lib/trpc.js';
import {
  useOAuthEntityContext,
  type OAuthEntityContext,
} from '../lib/auth/oauth_entity_context.js';
import { useAuth } from '../lib/auth/auth_context.js';
import { isWriteCapableGrant } from './use_pay_runs.js';

/** Group override methods — statutory types cannot be overridden per group. */
export type LeaveGroupAccrualMethod = 'fixed_annual' | 'monthly' | 'none';

export interface LeavePolicyGroupRule {
  leave_type: string;
  accrual_method: LeaveGroupAccrualMethod;
  /** Days × 100 per leave year; `null` = no grant for this group. */
  annual_entitlement_days_scaled: number | null;
  prorate_on_join: boolean;
  /** Days × 100; `null` = unlimited carry-forward. */
  carry_forward_max_days_scaled: number | null;
}

export interface LeavePolicyGroup {
  id: string;
  code: string;
  name: string;
  is_archived: boolean;
  rules: LeavePolicyGroupRule[];
  employee_ids: string[];
}

/** Wire shape — `accrual_method` arrives as a plain string. */
export interface RealLeavePolicyGroupRecord {
  id: string;
  code: string;
  name: string;
  is_archived: boolean;
  rules: Array<{
    leave_type: string;
    accrual_method: string;
    annual_entitlement_days_scaled: number | null;
    prorate_on_join: boolean;
    carry_forward_max_days_scaled: number | null;
  }>;
  employee_ids: string[];
}

export interface LeavePolicyGroupsInput {
  firm_id: string;
  entity_id: string;
}

export interface UpsertLeavePolicyGroupInput {
  code: string;
  name: string;
  is_archived?: boolean;
  rules: LeavePolicyGroupRule[];
}

export interface LeavePolicyGroupsDeps {
  listLeavePolicyGroups: (
    input: LeavePolicyGroupsInput,
  ) => Promise<{ groups: RealLeavePolicyGroupRecord[] }>;
  upsertLeavePolicyGroup: (
    input: LeavePolicyGroupsInput & UpsertLeavePolicyGroupInput,
  ) => Promise<{ id: string; created: boolean }>;
  setEmployeeLeavePolicyGroup: (
    input: LeavePolicyGroupsInput & {
      employee_id: string;
      group_code: string | null;
    },
  ) => Promise<{ ok: boolean }>;
}

function defaultDeps(): LeavePolicyGroupsDeps {
  const client = getTrpcClient();
  return {
    listLeavePolicyGroups: (input) =>
      client.payroll.listLeavePolicyGroups.query(input),
    upsertLeavePolicyGroup: (input) =>
      client.payroll.upsertLeavePolicyGroup.mutate(input),
    setEmployeeLeavePolicyGroup: (input) =>
      client.payroll.setEmployeeLeavePolicyGroup.mutate(input),
  };
}

function narrowMethod(method: string): LeaveGroupAccrualMethod {
  return method === 'fixed_annual' || method === 'monthly' ? method : 'none';
}

export function adaptLeavePolicyGroup(
  raw: RealLeavePolicyGroupRecord,
): LeavePolicyGroup {
  return {
    id: raw.id,
    code: raw.code,
    name: raw.name,
    is_archived: raw.is_archived,
    rules: raw.rules.map((r) => ({
      leave_type: r.leave_type,
      accrual_method: narrowMethod(r.accrual_method),
      annual_entitlement_days_scaled: r.annual_entitlement_days_scaled,
      prorate_on_join: r.prorate_on_join,
      carry_forward_max_days_scaled: r.carry_forward_max_days_scaled,
    })),
    employee_ids: raw.employee_ids,
  };
}

export type LeavePolicyGroupsLoadFailure = 'entitlement_required' | 'error';

export type LeavePolicyGroupsFetchResult =
  | { outcome: 'success'; groups: LeavePolicyGroup[] }
  | { outcome: 'failure'; failure: LeavePolicyGroupsLoadFailure };

export async function fetchLeavePolicyGroups(
  input: LeavePolicyGroupsInput,
  deps?: Pick<LeavePolicyGroupsDeps, 'listLeavePolicyGroups'>,
  isCancelled: () => boolean = () => false,
): Promise<LeavePolicyGroupsFetchResult> {
  try {
    const resolved = deps ?? defaultDeps();
    const { groups } = await resolved.listLeavePolicyGroups(input);
    return { outcome: 'success', groups: groups.map(adaptLeavePolicyGroup) };
  } catch (err) {
    if (!isCancelled()) {
      console.error('[wagepro-web] Failed to load leave policy groups:', err);
    }
    return {
      outcome: 'failure',
      failure: isPayrollAddonRequiredError(err)
        ? 'entitlement_required'
        : 'error',
    };
  }
}

export type LeavePolicyGroupWriteResult =
  | { outcome: 'success' }
  | { outcome: 'failure'; failure: 'forbidden' | 'invalid' | 'error' };

function classifyWriteFailure(err: unknown): 'forbidden' | 'invalid' | 'error' {
  if (isForbiddenTrpcError(err)) return 'forbidden';
  if (typeof err === 'object' && err !== null && 'data' in err) {
    const data = (err as { data?: { code?: unknown } }).data;
    if (data?.code === 'BAD_REQUEST') return 'invalid';
  }
  return 'error';
}

export async function upsertLeavePolicyGroup(
  entity: LeavePolicyGroupsInput,
  group: UpsertLeavePolicyGroupInput,
  deps?: Pick<LeavePolicyGroupsDeps, 'upsertLeavePolicyGroup'>,
): Promise<LeavePolicyGroupWriteResult> {
  try {
    const resolved = deps ?? defaultDeps();
    await resolved.upsertLeavePolicyGroup({ ...entity, ...group });
    return { outcome: 'success' };
  } catch (err) {
    console.error('[wagepro-web] leave: upsert policy group failed:', err);
    return { outcome: 'failure', failure: classifyWriteFailure(err) };
  }
}

export async function setEmployeeLeavePolicyGroup(
  entity: LeavePolicyGroupsInput,
  employee_id: string,
  group_code: string | null,
  deps?: Pick<LeavePolicyGroupsDeps, 'setEmployeeLeavePolicyGroup'>,
): Promise<LeavePolicyGroupWriteResult> {
  try {
    const resolved = deps ?? defaultDeps();
    await resolved.setEmployeeLeavePolicyGroup({
      ...entity,
      employee_id,
      group_code,
    });
    return { outcome: 'success' };
  } catch (err) {
    console.error('[wagepro-web] leave: assign policy group failed:', err);
    return { outcome: 'failure', failure: classifyWriteFailure(err) };
  }
}

export type LeavePolicyGroupsState =
  | { status: 'loading' }
  | { status: 'no_entity' }
  | { status: 'entitlement_required' }
  | { status: 'error' }
  | { status: 'ready'; groups: LeavePolicyGroup[] };

export function resolveLeavePolicyGroupsState(input: {
  entityContext: OAuthEntityContext;
  groups: LeavePolicyGroup[] | undefined;
  failure: LeavePolicyGroupsLoadFailure | undefined;
}): LeavePolicyGroupsState {
  if (input.entityContext.status === 'no_entity')
    return { status: 'no_entity' };
  if (input.groups !== undefined) {
    return { status: 'ready', groups: input.groups };
  }
  if (input.failure === 'entitlement_required') {
    return { status: 'entitlement_required' };
  }
  if (input.failure === 'error') return { status: 'error' };
  return { status: 'loading' };
}

export interface UseLeavePolicyGroupsResult {
  state: LeavePolicyGroupsState;
  groups: LeavePolicyGroup[];
  retry: () => void;
  upsert:
    | ((
        group: UpsertLeavePolicyGroupInput,
      ) => Promise<LeavePolicyGroupWriteResult>)
    | undefined;
  assign:
    | ((
        employee_id: string,
        group_code: string | null,
      ) => Promise<LeavePolicyGroupWriteResult>)
    | undefined;
}

export function useLeavePolicyGroups(): UseLeavePolicyGroupsResult {
  const entityContext = useOAuthEntityContext();
  const { grant } = useAuth();
  const [groups, setGroups] = React.useState<LeavePolicyGroup[] | undefined>(
    undefined,
  );
  const [failure, setFailure] = React.useState<
    LeavePolicyGroupsLoadFailure | undefined
  >(undefined);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    if (entityContext.status !== 'ready') return;
    const input = {
      firm_id: entityContext.firm_id,
      entity_id: entityContext.entity_id,
    };
    let cancelled = false;
    setGroups(undefined);
    setFailure(undefined);

    async function load(): Promise<void> {
      const result = await fetchLeavePolicyGroups(
        input,
        undefined,
        () => cancelled,
      );
      if (cancelled) return;
      if (result.outcome === 'success') setGroups(result.groups);
      else setFailure(result.failure);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [entityContext, reloadToken]);

  const state = React.useMemo(
    () => resolveLeavePolicyGroupsState({ entityContext, groups, failure }),
    [entityContext, groups, failure],
  );
  const retry = React.useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const readyEntity =
    state.status === 'ready' && entityContext.status === 'ready'
      ? {
          firm_id: entityContext.firm_id,
          entity_id: entityContext.entity_id,
        }
      : null;
  const canWrite = readyEntity !== null && isWriteCapableGrant(grant);

  const reloadOnSuccess = (
    result: LeavePolicyGroupWriteResult,
  ): LeavePolicyGroupWriteResult => {
    if (result.outcome === 'success') retry();
    return result;
  };

  return {
    state,
    groups: state.status === 'ready' ? state.groups : [],
    retry,
    upsert: canWrite
      ? (group) =>
          upsertLeavePolicyGroup(readyEntity, group).then(reloadOnSuccess)
      : undefined,
    assign: canWrite
      ? (employee_id, group_code) =>
          setEmployeeLeavePolicyGroup(
            readyEntity,
            employee_id,
            group_code,
          ).then(reloadOnSuccess)
      : undefined,
  };
}
